import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTable } from '../context/TableContext';
import { useOutlet } from '../../context/OutletContext';
import useOutletMenuItems from '../../hooks/useOutletMenuItems';
import './TakeOrder.css';

const TakeOrder = () => {
  const { tableId } = useParams();
  const navigate = useNavigate();
  const { getTable, createTableOrder } = useTable();
  const { selectedOutlet } = useOutlet();
  const { menuData } = useOutletMenuItems(selectedOutlet?.id);

  const [table, setTable] = useState(null);
  const [orderItems, setOrderItems] = useState([]);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');

  // Customer and order details
  const [orderType, setOrderType] = useState('dine_in'); // dine_in, pickup, delivery
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [specialInstructions, setSpecialInstructions] = useState('');

  useEffect(() => {
    setTable(getTable(tableId));
  }, [tableId]);

  const getAllItems = () => {
    if (!menuData?.categories) return [];
    const items = [];
    menuData.categories.forEach(cat => {
      cat.sections?.forEach(sec => {
        sec.subsections?.forEach(sub => {
          sub.veg?.items?.forEach(item => items.push({...item, category: cat.category, isVeg: true}));
          sub.nonVeg?.items?.forEach(item => items.push({...item, category: cat.category, isVeg: false}));
        });
      });
    });
    return items;
  };

  const filteredItems = getAllItems().filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = category === 'all' || item.category === category;
    return matchesSearch && matchesCategory;
  });

  const addItem = (item) => {
    const existing = orderItems.find(i => i.name === item.name);
    if (existing) {
      setOrderItems(orderItems.map(i => i.name === item.name ? {...i, quantity: i.quantity + 1} : i));
    } else {
      setOrderItems([...orderItems, {...item, quantity: 1}]);
    }
  };

  const updateQty = (name, delta) => {
    setOrderItems(orderItems.map(i => i.name === name ? {...i, quantity: Math.max(0, i.quantity + delta)} : i).filter(i => i.quantity > 0));
  };

  const total = orderItems.reduce((sum, i) => sum + (i.price * i.quantity), 0);

  const placeOrder = async () => {
    if (orderItems.length === 0) return alert('Add items first');

    // Validate customer details
    if (!customerName.trim()) {
      return alert('Please enter customer name');
    }
    if (!customerPhone.trim() || customerPhone.length < 10) {
      return alert('Please enter valid phone number (10 digits)');
    }

    const result = await createTableOrder(tableId, {
      items: orderItems,
      total,
      tax: total * 0.05,
      orderNumber: 'T' + Date.now().toString().slice(-6),
      orderType,
      customerName,
      customerPhone,
      specialInstructions
    });
    if (result.success) navigate(`/waiter/bill/${result.orderId}`);
  };

  return (
    <div className="take-order-page">
      <div className="menu-side">
        <input placeholder="Search items..." value={search} onChange={(e) => setSearch(e.target.value)} />
        <div className="categories">
          <button className={category === 'all' ? 'active' : ''} onClick={() => setCategory('all')}>All</button>
          {menuData?.categories?.map(c => (
            <button key={c.category} className={category === c.category ? 'active' : ''} onClick={() => setCategory(c.category)}>{c.category}</button>
          ))}
        </div>
        <div className="items-grid">
          {filteredItems.map((item, i) => (
            <div key={i} className="item-card" onClick={() => addItem(item)}>
              <img src={item.image || '/placeholder.png'} alt={item.name} />
              <h4>{item.name}</h4>
              <p>₹{item.price}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="order-side">
        <h2>Table {table?.tableNumber}</h2>

        {/* Order Type Selection */}
        <div className="order-type-section">
          <label>Order Type:</label>
          <div className="order-type-buttons">
            <button
              className={orderType === 'dine_in' ? 'active' : ''}
              onClick={() => setOrderType('dine_in')}
            >
              🍽️ Dine In
            </button>
            <button
              className={orderType === 'pickup' ? 'active' : ''}
              onClick={() => setOrderType('pickup')}
            >
              🛍️ Pickup
            </button>
            <button
              className={orderType === 'delivery' ? 'active' : ''}
              onClick={() => setOrderType('delivery')}
            >
              🚴 Delivery
            </button>
          </div>
        </div>

        {/* Customer Details */}
        <div className="customer-details">
          <input
            type="text"
            placeholder="Customer Name *"
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value)}
            className="customer-input"
          />
          <input
            type="tel"
            placeholder="Phone Number (10 digits) *"
            value={customerPhone}
            onChange={(e) => setCustomerPhone(e.target.value)}
            maxLength="10"
            className="customer-input"
          />
          <textarea
            placeholder="Special Instructions (optional)"
            value={specialInstructions}
            onChange={(e) => setSpecialInstructions(e.target.value)}
            rows="2"
            className="customer-input"
          />
        </div>

        {/* Order Items List */}
        <div className="order-items">
          {orderItems.map((item, i) => (
            <div key={i} className="order-item">
              <span>{item.name}</span>
              <div>
                <button onClick={() => updateQty(item.name, -1)}>-</button>
                <span>{item.quantity}</span>
                <button onClick={() => updateQty(item.name, 1)}>+</button>
              </div>
              <span>₹{item.price * item.quantity}</span>
            </div>
          ))}
        </div>

        <div className="total">Total: ₹{total.toFixed(2)}</div>
        <button className="place-btn" onClick={placeOrder}>Place Order</button>
      </div>
    </div>
  );
};

export default TakeOrder;
